
import React, { createContext, useContext, useEffect, useState } from "react";
import { useThemeManager } from "@/hooks/useThemeManager";

type Theme = "dark" | "light";

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({
  children,
  defaultTheme = "light",
}: ThemeProviderProps) {
  console.log('[THEME-PROVIDER] Component mounting/re-rendering', {
    defaultTheme,
    timestamp: new Date().toISOString()
  });

  // Keep light/dark theme in localStorage (this remains per-user)
  const [theme, setTheme] = React.useState<Theme>(
    () => (localStorage.getItem("theme") as Theme) || defaultTheme
  );
  
  // Use global theme color from database
  const { globalPrimaryColor: primaryColor, setGlobalTheme: setPrimaryColor, globalThemeLoading: loading } = useThemeManager();

  // Add mount/unmount logging
  React.useEffect(() => {
    console.log('[THEME-PROVIDER] Component mounted', {
      theme,
      primaryColor,
      loading,
      timestamp: new Date().toISOString()
    });
    
    return () => {
      console.log('[THEME-PROVIDER] Component unmounting', {
        timestamp: new Date().toISOString()
      });
    };
  }, []);

  // Log when context value changes
  React.useEffect(() => {
    console.log('[THEME-PROVIDER] Context value changed', {
      theme,
      primaryColor,
      loading,
      timestamp: new Date().toISOString()
    });
  }, [theme, primaryColor, loading]);

  useEffect(() => {
    console.log('[THEME-PROVIDER] Theme change detected:', {
      newTheme: theme,
      timestamp: new Date().toISOString()
    });
    
    const root = window.document.documentElement;
    console.log('[THEME-PROVIDER] Root element classes before:', root.classList.toString());
    
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
    
    console.log('[THEME-PROVIDER] Root element classes after:', root.classList.toString());
    
    // Check if CSS variables are properly set
    const computedStyle = getComputedStyle(root);
    console.log('[THEME-PROVIDER] CSS Variables check:', {
      '--background': computedStyle.getPropertyValue('--background'),
      '--foreground': computedStyle.getPropertyValue('--foreground'),
      '--input': computedStyle.getPropertyValue('--input'),
      '--primary': computedStyle.getPropertyValue('--primary'),
    });
  }, [theme]);

  console.log('[THEME-PROVIDER] Rendering with:', {
    theme,
    primaryColor,
    loading,
    timestamp: new Date().toISOString()
  });

  const value = {
    theme,
    setTheme,
    primaryColor,
    setPrimaryColor,
    loading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = (): ThemeContextType => {
  console.log('[USE-THEME] Hook called', {
    timestamp: new Date().toISOString(),
    stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
  });
  
  const context = useContext(ThemeContext);
  
  console.log('[USE-THEME] Context value:', {
    hasContext: context !== undefined,
    context: context ? { theme: context.theme, primaryColor: context.primaryColor, loading: context.loading } : null,
    timestamp: new Date().toISOString()
  });
  
  if (context === undefined) {
    console.error('[USE-THEME] Context is undefined - throwing error', {
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack
    });
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
